/**
 * 67.36, the wind you can see in what sways (lane E): the grass and the flowers (groundScatter's swaying kinds) and the
 * trees (treeStands) read one set of uniforms, fed from the wind where the viewer is — with decision 120's switch on, the
 * field (core/wind.ts windAt: 62.54's bands and cells, the gusts, the shelter): its direction along the ground and its
 * level; with it off, the weather's breeze along the match's seeded direction, as before. On top, the gusts' **fronts**:
 * bands of a harder push lying across the wind, wavy and patchy along their length, rolling downwind at a pace that
 * follows the wind (a breeze's fronts creep, a gale's race) — a gust is seen crossing the meadow and bowing the stand in
 * turn. A blade bent by a front shows its paler side (`sheen`). The direction and the level ease (`turnS`, `easeS`), so a
 * cell passing over is the meadow slowly turning, not a jump. Presentation only; nothing here reaches the sim.
 */
import * as THREE from 'three';

export const WIND_SWAY = {
  /** a blade's tip moves this share of its height at full wind (groundScatter's SWAY.bend) */
  bend: 0.45,
  /** a front's spacing along the wind (people), and a patch's length along a front (× that) */
  frontPeople: 6, patch: 2.5,
  /** how far the fronts travel a second (people): at a calm and at a storm's wind, eased between by the level */
  pace: { calm: 0.6, gale: 4.5 },
  /** a front's share of the push (the rest is the wind's steady lean) */
  front: 0.55,
  /** a blade bent by a front shows its paler side: toward `pale` by this share at a front's height in a fresh wind or more */
  sheen: 0.45, pale: [0.62, 0.66, 0.42] as const,
  /** seconds the direction and the level take to follow the wind (a cell's turn, a gust's swell) */
  turnS: 1.5, easeS: 2.5,
};

/** The uniforms every swaying material shares (the wind's direction in the world, along the ground; its level 0–1; the fronts' spacing and travel, world units). */
export interface WindSwayUniforms { uTime: { value: number }; uWind: { value: number }; uWindDir: { value: THREE.Vector3 }; uGust: { value: number }; uFront: { value: number } }

/** Fresh uniforms for a world whose person is `person` world units tall, the wind `level` along `dir` (the match's seeded way). */
export function windSwayUniforms(person: number, dir: THREE.Vector3, level = 0): WindSwayUniforms {
  return { uTime: { value: 0 }, uWind: { value: level }, uWindDir: { value: dir.clone().normalize() }, uGust: { value: WIND_SWAY.frontPeople * person }, uFront: { value: 0 } };
}

/**
 * One frame of the wind in what sways: the level eases toward `level`, the direction turns toward `dir` (world, along the
 * ground; null keeps the way it blows — the switch off, or a calm), and the fronts travel downwind at the level's pace.
 * `person` is the person's height in world units.
 */
export function stepWindSway(u: WindSwayUniforms, dir: { x: number; y: number; z: number } | null, level: number, dt: number, person: number): void {
  u.uTime.value += dt;
  u.uWind.value += (level - u.uWind.value) * Math.min(1, dt / WIND_SWAY.easeS);
  if (dir && dir.x * dir.x + dir.y * dir.y + dir.z * dir.z > 1e-18) {
    _to.set(dir.x, dir.y, dir.z).normalize();
    u.uWindDir.value.lerp(_to, Math.min(1, dt / WIND_SWAY.turnS));
    if (u.uWindDir.value.lengthSq() < 1e-6) u.uWindDir.value.copy(_to);   // (a turn right round: take the new way)
    u.uWindDir.value.normalize();
  }
  const P = WIND_SWAY.pace;
  u.uFront.value += (P.calm + (P.gale - P.calm) * Math.max(0, Math.min(1, u.uWind.value))) * person * dt;
}
const _to = new THREE.Vector3();

/** The uniforms' declarations, for a vertex shader's head (uTime is the material's own). */
export const WIND_SWAY_HEAD = 'uniform float uWind;\nuniform vec3 uWindDir;\nuniform float uGust;\nuniform float uFront;\n';
/**
 * The fronts' push at a place (0–1): `base` the piece's world place, `up` its up, `w` the wind along its ground (unit),
 * `jitter` its own 0–1. Fronts lie across the wind, `uGust` apart, wavy along their length and stronger in patches,
 * travelling downwind with `uFront`. `windFrontAt` the same fronts `gust` apart (the trees' are wider: treeStands TREE_SWAY).
 */
export const WIND_FRONT_GLSL = `
float windFrontAt(vec3 base, vec3 up, vec3 w, float jitter, float gust) {
  vec3 across = cross(up, w);
  float a = dot(base, across) / gust, d = (dot(base, w) - uFront) / gust;
  float wave = 0.5 + 0.5 * sin(d * 6.2831853 + 0.9 * sin(a * 1.3) + jitter * 0.6);
  float clump = 0.55 + 0.45 * sin(a * ${(2 * Math.PI / WIND_SWAY.patch).toFixed(5)} + uFront / gust * 0.37);   // ('patch' is a reserved word in GLSL ES 3)
  return wave * clump;
}
float windFront(vec3 base, vec3 up, vec3 w, float jitter) { return windFrontAt(base, up, w, jitter, uGust); }
`;
